import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { FilterConfig } from './ColumnFilter';
import { formatFilterChipLabel, isFilterValueActive, toDateInputValue } from '../utils/filterUtils';

interface ReportFilterBarProps {
  configs: FilterConfig[];
  values: Record<string, any>;
  onChange: (field: string, value: any) => void;
  onSearch: () => void;
  onClear: () => void;
  searchDisabled?: boolean;
  isSearching?: boolean;
  extraActions?: React.ReactNode;
  resultCount?: number;
  hasSearched?: boolean;
}

const inputSx = {
  width: '100%',
  '& .MuiInputBase-root': {
    backgroundColor: 'background.paper'
  }
};

const ReportFilterBar: React.FC<ReportFilterBarProps> = ({
  configs,
  values,
  onChange,
  onSearch,
  onClear,
  searchDisabled = false,
  isSearching = false,
  extraActions,
  resultCount = 0,
  hasSearched = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(true);

  const activeConfigs = configs.filter((config) => isFilterValueActive(values[config.field]));
  const activeCount = activeConfigs.length;

  useEffect(() => {
    if (isMobile && hasSearched && !isSearching) {
      setExpanded(false);
    }
  }, [isMobile, hasSearched, isSearching]);

  const handleClear = () => {
    onClear();
    setExpanded(true);
  };

  const handleClearField = (field: string) => {
    onChange(field, '');
  };

  const renderPair = (left: React.ReactNode, right: React.ReactNode) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1
      }}
    >
      {left}
      {right}
    </Box>
  );

  const renderField = (config: FilterConfig) => {
    const value = values[config.field];
    const useAutocomplete = config.autocomplete || (config.options && config.options.length > 8);
    const dateMin = toDateInputValue(config.min);
    const dateMax = toDateInputValue(config.max);

    let field: React.ReactNode = null;

    switch (config.type) {
      case 'string':
        field = (
          <TextField
            size="small"
            label={config.label}
            value={value || ''}
            onChange={(e) => onChange(config.field, e.target.value)}
            placeholder={config.placeholder || `Filter by ${config.label}`}
            sx={inputSx}
          />
        );
        break;
      case 'number':
        if (!config.range) {
          field = (
            <TextField
              size="small"
              label={config.label}
              type="number"
              value={value || ''}
              onChange={(e) => onChange(config.field, e.target.value)}
              placeholder={config.placeholder || `Filter by ${config.label}`}
              sx={inputSx}
            />
          );
        } else {
          field = renderPair(
            <TextField
              size="small"
              label={`${config.label} min`}
              type="number"
              value={value?.min ?? ''}
              onChange={(e) => onChange(config.field, { ...(value || {}), min: e.target.value })}
              placeholder={config.min != null ? String(config.min) : 'Min'}
              inputProps={{ min: config.min ?? undefined, max: config.max ?? undefined }}
              sx={inputSx}
            />,
            <TextField
              size="small"
              label={`${config.label} max`}
              type="number"
              value={value?.max ?? ''}
              onChange={(e) => onChange(config.field, { ...(value || {}), max: e.target.value })}
              placeholder={config.max != null ? String(config.max) : 'Max'}
              inputProps={{ min: config.min ?? undefined, max: config.max ?? undefined }}
              sx={inputSx}
            />
          );
        }
        break;
      case 'date':
        field = renderPair(
          <TextField
            size="small"
            label={`${config.label} from`}
            type="date"
            value={value?.start || ''}
            onChange={(e) => onChange(config.field, { ...(value || {}), start: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dateMin || undefined, max: dateMax || undefined }}
            sx={inputSx}
          />,
          <TextField
            size="small"
            label={`${config.label} to`}
            type="date"
            value={value?.end || ''}
            onChange={(e) => onChange(config.field, { ...(value || {}), end: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dateMin || undefined, max: dateMax || undefined }}
            sx={inputSx}
          />
        );
        break;
      case 'select':
        if (useAutocomplete) {
          const selected = config.options?.find((option) => option.value === (value || '')) || null;
          field = (
            <Autocomplete
              size="small"
              options={config.options || []}
              getOptionLabel={(option) => option.label}
              value={selected}
              onChange={(_event, newValue) => onChange(config.field, newValue ? newValue.value : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={config.label}
                  placeholder={config.placeholder || `Filter by ${config.label}`}
                />
              )}
              sx={inputSx}
            />
          );
        } else {
          field = (
            <FormControl size="small" sx={inputSx}>
              <InputLabel>{config.label}</InputLabel>
              <Select
                value={value || ''}
                label={config.label}
                onChange={(e: SelectChangeEvent) => onChange(config.field, e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {config.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        break;
      default:
        field = null;
    }

    return (
      <Box key={config.field} sx={{ minWidth: 0 }}>
        {field}
      </Box>
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        overflow: 'hidden',
        borderRadius: 2
      }}
    >
      {isSearching && <LinearProgress />}

      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
            {isSearching && (
              <Chip size="small" color="primary" label="Loading results..." />
            )}
            {!isSearching && hasSearched && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${resultCount} row${resultCount === 1 ? '' : 's'} retrieved`}
              />
            )}
          </Box>
          {isMobile && (
            <Button size="small" onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? 'Hide' : 'Show'}
            </Button>
          )}
        </Box>

        {activeCount > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            {activeConfigs.map((config) => (
              <Chip
                key={config.field}
                size="small"
                variant="outlined"
                label={formatFilterChipLabel(config.label, values[config.field])}
                onDelete={() => handleClearField(config.field)}
                sx={{ maxWidth: '100%' }}
              />
            ))}
          </Box>
        )}

        <Collapse in={!isMobile || expanded} timeout="auto">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))'
              },
              gap: 1.5
            }}
          >
            {configs.map(renderField)}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'stretch', sm: 'flex-start' },
              gap: 1,
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <Button
              variant="contained"
              onClick={onSearch}
              disabled={searchDisabled || isSearching}
              fullWidth={isMobile}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={isSearching}
              fullWidth={isMobile}
            >
              Clear
            </Button>
            {extraActions}
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

export default ReportFilterBar;
